import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Grid, List, Search, Plus, Eye } from 'lucide-react';
import { toast } from '../utils/toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DemoPage = () => {
  const navigate = useNavigate();
  const [demoRequests, setDemoRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchDemoRequests();
  }, []);

  useEffect(() => {
    applySearch();
  }, [searchTerm, demoRequests]);

  const fetchDemoRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/demo-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDemoRequests(response.data || []);
      setFilteredRequests(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch demo requests:', error);
      toast.error('Failed to load demo requests');
      setLoading(false);
    }
  };

  const applySearch = () => {
    if (!searchTerm) {
      setFilteredRequests(demoRequests);
      return;
    }

    const filtered = demoRequests.filter(req => 
      req.organization_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.contact_person.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.phone.includes(searchTerm)
    );
    setFilteredRequests(filtered);
  };

  const handleAddToLead = (demo) => {
    // If demo already has a linked lead, navigate to view it
    if (demo.linked_lead_id) {
      navigate(`/crm/leads/${demo.linked_lead_id}`);
      return;
    }
    
    // Navigate to Add Lead form with demo data pre-filled
    navigate('/crm/leads/add', { 
      state: { 
        demoData: {
          hospital_name: demo.institution_name,
          contact_email: demo.email,
          contact_phone: demo.phone_number,
          number_of_beds: demo.number_of_beds,
          number_of_staff: demo.number_of_staff,
          demo_id: demo.id,
          contact_person_name: demo.full_name
        }
      }
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading demo requests...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Page Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">DEMO</h1>
            <p className="text-sm text-gray-600 mt-1">Showing {filteredRequests.length} of {demoRequests.length} entries</p>
          </div>

          <div className="flex items-center space-x-4">
            {/* View Toggle */}
            <div className="flex bg-white border border-gray-300 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-[#0056B3] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-[#0056B3] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>

            {/* Search */}
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0056B3]"
              />
            </div>
          </div>
        </div>

        {/* Grid View */}
        {viewMode === 'grid' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRequests.map((request) => (
              <div key={request.id} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                {/* Institution Name - Prominent */}
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <h3 className="text-[18px] font-bold text-[#0056B3] mb-2">
                    {request.hospital_name || request.institution_name}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      request.type === 'demo' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                    }`}>
                      {request.type === 'demo' ? 'Demo Request' : 'Contact Us'}
                    </span>
                  </div>
                </div>
                
                {/* Demo Contact Person Details */}
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-1">Demo Contact Person</p>
                  <p className="text-md font-semibold text-gray-900">{request.full_name || request.contact_person}</p>
                  <p className="text-sm text-gray-600">{request.email}</p>
                  <p className="text-sm text-gray-600">{request.phone_number || request.phone}</p>
                </div>
                
                {/* CRM Officer Details - Only if linked to lead */}
                {request.linked_lead_id && request.crm_owner_name && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-700 font-semibold mb-1">CRM Officer/Associate</p>
                    <p className="text-sm font-medium text-blue-900">{request.crm_owner_name}</p>
                    {request.crm_owner_email && (
                      <p className="text-xs text-blue-700">{request.crm_owner_email}</p>
                    )}
                  </div>
                )}
                
                <Button
                  onClick={() => handleAddToLead(request)}
                  className={request.linked_lead_id 
                    ? "w-full bg-[#0056B3] text-white hover:bg-[#0056B3]/90"
                    : "w-full border-2 border-dashed border-green-500 bg-white text-green-600 hover:bg-green-50"
                  }
                >
                  {request.linked_lead_id ? (
                    <>
                      <Eye className="w-4 h-4 mr-2" />
                      View Lead
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Add to Lead
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#0056B3] text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">INSTITUTION NAME</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">CONTACT PERSON</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">EMAIL</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">PHONE</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">CRM OFFICER</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">TYPE</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.map((request, index) => (
                    <tr key={request.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-6 py-4 text-sm font-medium text-[#0056B3]">
                        {request.hospital_name || request.institution_name}
                      </td>
                      <td className="px-6 py-4 text-sm">{request.full_name || request.contact_person}</td>
                      <td className="px-6 py-4 text-sm">{request.email}</td>
                      <td className="px-6 py-4 text-sm">{request.phone_number || request.phone}</td>
                      <td className="px-6 py-4 text-sm">
                        {request.crm_owner_name ? (
                          <div>
                            <p className="font-medium text-gray-900">{request.crm_owner_name}</p>
                            {request.crm_owner_email && (
                              <p className="text-xs text-gray-600">{request.crm_owner_email}</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">Not assigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          request.type === 'demo' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                        }`}>
                          {request.type === 'demo' ? 'Demo Request' : 'Contact Us'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <Button
                          onClick={() => handleAddToLead(request)}
                          size="sm"
                          className={request.linked_lead_id 
                            ? "bg-[#0056B3] text-white hover:bg-[#0056B3]/90"
                            : "border-2 border-dashed border-green-500 bg-white text-green-600 hover:bg-green-50"
                          }
                        >
                          {request.linked_lead_id ? (
                            <>
                              <Eye className="w-4 h-4 mr-1" />
                              View Lead
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-1" />
                              Add to Lead
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredRequests.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No demo requests found</p>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {filteredRequests.length === 0 && viewMode === 'grid' && (
          <div className="text-center py-12">
            <p className="text-gray-500">No demo requests found</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default DemoPage;
